import { IAlertProps, IBaseAlertProps } from "@components/alert/Alert.types";
import { getDrillDownLink } from "@components/drillDown/DrillDown.utils";
import { formatMinutesToHoursString } from "@components/inputs/date/utils";
import { getPluralizationLabel, IGetValueArgs, TGetValueFn } from "@components/smart/FieldInfo";
import { IEditableAmountDialogData } from "@components/smart/selectionTableView/EditableAmountDialog";
import {
    editableAmountLinkFormatter,
    ISelectionWithEditableAmountCustomTableData
} from "@components/smart/selectionTableView/SelectionTableView.utils";
import { ISmartODataTableAPI, TSmartODataTableStorage } from "@components/smart/smartTable/SmartODataTableBase";
import { TFormatterFn } from "@components/smart/smartTable/SmartTable.utils";
import { ITabData } from "@components/tabs";
import BindingContext, { createBindingContext } from "@odata/BindingContext";
import { isODataError, ODataError } from "@odata/Data.types";
import { getRouteByEntityType } from "@odata/EntityTypes";
import { getPropertyTranslationSync } from "@odata/FieldInfo.utils";
import {
    EntitySetName,
    EntityTypeName,
    IDocumentEntity,
    IPrPayrollPeriodEntity,
    IPrPayslipBalancingPeriodEntity,
    IPrPayslipDeductionEntity,
    IPrPayslipEmploymentEntity,
    IPrPayslipEntity,
    IPrPayslipMasterDataChangeEntity,
    IPrWorkingPatternEntity,
    PrAttendanceEntity,
    PrPayrollPeriodEntity,
    PrPayslipEmploymentEntity,
    PrPayslipEntity
} from "@odata/GeneratedEntityTypes";
import {
    PrAttendanceStatusCode,
    PrDeductionTypeCode,
    PrPayrollPeriodStatusCode,
    PrPayslipEmploymentItemTypeCode,
    PrRegularSalaryTypeCode,
    PrSalaryComponentTypeCode
} from "@odata/GeneratedEnums";
import { OData } from "@odata/OData";
import { getEntitySetFromEntityType } from "@odata/OData.utils";
import { parseResponse } from "@odata/ODataParser";
import { getEmployeeSalaryCurrencyPath } from "@pages/payroll/employee/Employee.utils";
import { IWithPayrollPeriodsCustomData } from "@pages/payroll/payroll.types";
import { fetchPayrollPeriods } from "@pages/payroll/payroll.utils";
import type { IBulkAddPayslipTarget } from "@pages/payroll/payslip/bulkAddItems/BulkAddPayslipItems.utils";
import { IFormTableListRow, IFormTableListSection } from "@pages/payroll/payslip/formTableList/FormTableList";
import { PlaceholderWrapper } from "@pages/payroll/payslip/formTableList/FormTableList.styles";
import { isStartOfEndOfMonth } from "@pages/payroll/payslip/overview/TotalOverview.def";
import { DetectedChangeLine, TabsStyled } from "@pages/payroll/payslip/Payslip.styles";
import { IPrPayslipFormStorageCustomData } from "@pages/payroll/payslip/PayslipFormView";
import customFetch, { getDefaultPostParams } from "@utils/customFetch";
import { isDefined } from "@utils/general";
import { logger } from "@utils/log";
import memoize from "@utils/memoize";
import { universalPersonNameFormatter } from "@utils/string";
import i18next from "i18next";
import React, { ReactElement } from "react";

import { DASH_CHARACTER, PAYROLL_REST_API_URL, REST_API_URL } from "../../../constants";
import { BackendErrorCode, Status, TextAlign } from "../../../enums";
import { TableStorage } from "../../../model/TableStorage";
import TestIds from "../../../testIds";
import DateType, { DateFormat, getUtcDayjs } from "../../../types/Date";
import { getAlertFromError, hasNestedError } from "../../../views/formView/Form.utils";
import { FormStorage } from "../../../views/formView/FormStorage";
import { ITableViewBaseProps } from "../../../views/table/TableView";
import { getTableViewMessage } from "../../../views/table/TableView.utils";

export enum BulkAddItemType {
    SalaryComponent = "SalaryComponent",
    Benefit = "Benefit",
    ExtraSalary = "ExtraSalary",
    RetirementSaving = "RetirementSaving",
}

export interface IPrPayslipTableStorageCustomData extends IWithPayrollPeriodsCustomData {
    showDelayedIncomeDialog?: boolean;
    showBulkAddPayslipItemsDialog?: boolean;
    bulkAddItemType?: BulkAddItemType;
    bulkAddPayslipTargets?: IBulkAddPayslipTarget[];
    submissionType?: string;
}

export enum DialogType {
    SalaryComponent = "SalaryComponent",
    ExtraSalary = "ExtraSalary",
    Receivables = "Receivables",
    Liabilities = "Liabilities",
    PayslipManualChange = "PayslipManualChange",
    Benefits = "Benefits",
}

export enum UnseizableTotalAmount {
    Own = "Own",
    Calculated = "Calculated",
}

const RESET_PAYSLIP_API_URL = `${PAYROLL_REST_API_URL}/RecalculatePayslip`;
const SEND_PAYSLIP_EMAIL_URL = `${REST_API_URL}/PrPayslipEmail`;

export const productPath = BindingContext.localContext("product");
export const hasUnseizableOverridePath = BindingContext.localContext("hasUnseizableOverride");

/**
 *  *** Employment Tabs ***
 **/
export const currentEmploymentPath = BindingContext.localContext("currentEmployment");
export const tabsPath = BindingContext.localContext("employmentTabs");

export function getPayslipEmploymentTabId(employment: IPrPayslipEmploymentEntity): string {
    return employment?.Id?.toString();
}

export const getPeriodForPayslip = memoize((oData: OData, payslip: IPrPayslipEntity) => {
    const { Month: month, Year: year } = payslip;
    return fetchPayrollPeriods(oData, { month, year });
}, (_, payslip) => [payslip.Year, payslip.Month]);

export function getCurrentEmploymentBc(storage: FormStorage<IPrPayslipEntity>): BindingContext {
    return storage.data.bindingContext.navigate(PrPayslipEntity.Employments)
            .addKey(storage.getValueByPath(currentEmploymentPath));
}

export const isPayslipEmploymentVisible = ({ bindingContext, storage }: IGetValueArgs): boolean =>
        bindingContext.getKey()?.toString() === storage.getValueByPath(currentEmploymentPath);

export function setCurrentPayslipEmploymentTabId(storage: FormStorage<IPrPayslipEntity>, tabId: string): void {
    storage.setValueByPath(currentEmploymentPath, tabId);
    storage.refresh(true);
}

export const getEmploymentName = (empl: IPrPayslipEmploymentEntity, showSmallScaleSuffix = true): string => {
    if (!empl) {
        return "-";
    }
    const key = `Payslip:Form.EmpType_${empl.Employment.TypeCode}`;
    const data = { number: empl.Employment?.NumberOurs };
    const employmentName = i18next.exists(key)
        ? i18next.t(key, data)
        : i18next.t("Payslip:Form.EmpType_Employment", data);

    if (!empl.IsSmallScaleEmployment || !showSmallScaleSuffix) {
        return employmentName;
    }

    return `${employmentName} - ${i18next.t("Payslip:Form.EmpType_SmallScaleEmploymentSuffix")}`;
};

export function isAttendanceClosed(storage: FormStorage<IPrPayslipEntity>, bc: BindingContext): boolean {
    const employment = storage.getValue(bc) as IPrPayslipEmploymentEntity;
    return employment?.Attendance?.StatusCode !== PrAttendanceStatusCode.ToProcess;
}

export function isAnyAttendanceOpen(storage: FormStorage<IPrPayslipEntity>): boolean {
    const { Employments } = storage.data.entity;
    return Employments?.some(employment =>
            employment.Attendance.StatusCode === PrAttendanceStatusCode.ToProcess);
}

export const hasOpenAttendance: TGetValueFn<boolean> = (args) => isAnyAttendanceOpen(args.storage as FormStorage<IPrPayslipEntity>);

export const isDelayedIncome: TGetValueFn<boolean> = (args) => !!args.storage.getValueByPath(PrPayslipEntity.IsDelayedIncome);

export const isCurrentAttendanceClosed: TGetValueFn<boolean> = (args) =>
    isAttendanceClosed(args.storage as FormStorage<IPrPayslipEntity>, getCurrentEmploymentBc(args.storage as FormStorage<IPrPayslipEntity>));

export function renderPayslipEmploymentTabs(employments: IPrPayslipEmploymentEntity[], storage: FormStorage<IPrPayslipEntity>): ReactElement {
    const isAnyOpen = isAnyAttendanceOpen(storage);
    const employmentsBc = storage.data.bindingContext.navigate(PrPayslipEntity.Employments);
    const additionalFieldData = storage.data.additionalFieldData ?? {};

    const data = employments?.map((pe): ITabData => {
        const status = isAnyOpen ? pe?.Attendance?.StatusCode === PrAttendanceStatusCode.ToProcess ? Status.Warning : Status.Success : undefined;
        const hasError = hasNestedError(additionalFieldData, employmentsBc.addKey(pe));
        return {
            id: getPayslipEmploymentTabId(pe),
            title: getEmploymentName(pe, employments?.length === 1),
            status,
            hasError
        };
    });

    return (<div>
        {isAnyOpen && <PlaceholderWrapper data-testid={TestIds.Text}>{storage.t("Payslip:Form.CloseAllAttendances")}</PlaceholderWrapper>}
        <TabsStyled data={data}
                    onChange={setCurrentPayslipEmploymentTabId.bind(null, storage)}
                    selectedTabId={storage.getValueByPath(currentEmploymentPath)}/>
    </div>);
}

export function getPayslipPeriodFilter(period: IPrPayrollPeriodEntity): string {
    return period ? `${PrPayslipEntity.Month} eq ${period.Month} and ${PrPayslipEntity.Year} eq ${period.Year}` : null;
}

export async function fetchUnfinishedPayslipsCount(oData: OData, period: IPrPayrollPeriodEntity): Promise<number> {
    const periodFilter = getPayslipPeriodFilter(period);
    const anyConditionFilter =
            `${PrPayslipEntity.Employments}/any(e: e/${PrPayslipEmploymentEntity.Attendance}/${PrAttendanceEntity.StatusCode} eq '${PrAttendanceStatusCode.ToProcess}')`;

    const query = oData.getEntitySetWrapper(EntitySetName.PrPayslips).query()
            .select(PrPayrollPeriodEntity.Id, PrPayrollPeriodEntity.Month, PrPayrollPeriodEntity.Year)
            .filter(`${periodFilter} AND (${anyConditionFilter})`)
            .count();
    try {
        const res = await query.fetchData<IPrPayslipEntity>();
        return res._metadata.count;
    } catch(e) {
        logger.error("Error fetching unfinished payslips count", e);
    }
    return 0;
}

export async function setPayrollPeriodStatus(storage: TableStorage<ISmartODataTableAPI, IPrPayslipTableStorageCustomData>, period: IPrPayrollPeriodEntity, status: PrPayrollPeriodStatusCode): Promise<true | IBaseAlertProps> {
    let alert: IBaseAlertProps;
    try {
        await storage.oData.getEntitySetWrapper(EntitySetName.PrPayrollPeriods).update(period.Id, { StatusCode: status });
        period.StatusCode = status;
        if (period.Status) {
            period.Status.Code = status;
        }
        return true;
    } catch (e) {
        let errors: string[];
        if (isODataError(e)) {
            if (e._code === BackendErrorCode.ValidationError) {
                const errMap = new Set<string>();
                e._validationMessages?.forEach((m) =>
                        errMap.add(m.message));
                errors = Array.from(errMap);
            } else {
                errors = [e._message];
            }
        }
        alert = {
            status: Status.Error,
            title: i18next.t("Common:Errors.ErrorHappened"),
            subTitle: errors ?? i18next.t("Common:Errors.InternalError"),
        };
    }
    return alert;
}

export const dayFormatter = (value: number): string => `${value} ${i18next.t("Common:General.Day")}`;
export const fullDayFormatter = (value: number): string => i18next.t("Common:General.XDays", { count: value });
export const hoursFormatter = (value: number): string => i18next.t("Common:General.Hours", { hours: value });

export const AttendanceOverviewProps = [
    { id: PrPayslipEmploymentEntity.PlannedWork },
    { id: PrPayslipEmploymentEntity.WorkedIncludingHolidays },
    { id: PrPayslipEmploymentEntity.HolidayCarriedOver },
    { id: PrPayslipEmploymentEntity.HolidayCompensatory },
    { id: PrPayslipEmploymentEntity.HolidayPrevious },
    { id: PrPayslipEmploymentEntity.OvertimeCarriedOver },
    { id: PrPayslipEmploymentEntity.OvertimeCompensatory },
    { id: PrPayslipEmploymentEntity.OvertimePrevious },
    { id: PrPayslipEmploymentEntity.OvertimeThisMonth },
    { id: PrPayslipEmploymentEntity.HolidayWorked },
];

export function getAttendanceOverviewSections(storage: FormStorage<IPrPayslipEntity>, bc: BindingContext): IFormTableListSection[] {
    const payslipEmployment = storage.getValue(bc) as IPrPayslipEmploymentEntity;

    const worked = payslipEmployment[PrPayslipEmploymentEntity.WorkedIncludingHolidays];
    const notWorked = -1 * Math.min(payslipEmployment.Attendance.Balance, 0);
    // const overtime = Math.max(payslipEmployment.Attendance.Balance, 0);
    const dayItem = payslipEmployment.Items?.find(item => item.TypeCode === PrPayslipEmploymentItemTypeCode.DaysWorked);
    const workedDays = dayItem?.IntValue ?? 0;

    const customData = storage.getCustomData() as IPrPayslipFormStorageCustomData;
    const periodWorkDays = customData.CurrentPeriodWorkDays;
    const periodHolidays = customData.CurrentPeriodHolidays;

    return [{
        rows: [
            { title: i18next.t("Payslip:Form.AttendanceWorkedDays"), value: workedDays, formatter: dayFormatter },
            { title: i18next.t("Payslip:Form.AttendanceWorked"), value: worked, formatter: formatMinutesToHoursString },
            notWorked && { title: i18next.t("Payslip:Form.AttendanceNotWorked"), value: notWorked, formatter: formatMinutesToHoursString },
            // overtime && { title: i18next.t("Payslip:Form.OvertimeWorked"), value: overtime, formatter: formatMinutesToHoursString },
            periodWorkDays && { title: i18next.t("Payslip:Attendance.WorkFund"), value: periodWorkDays, formatter: dayFormatter },
            periodHolidays && { title: i18next.t("Payslip:Attendance.Holidays"), value: periodHolidays, formatter: dayFormatter },
        ].filter(Boolean) as IFormTableListRow[] // filter out undefined values
    }];
}

export const hasBalancingPeriodOverview = ({ bindingContext, storage }: IGetValueArgs): boolean => {
    const payslipEmployment = storage.getValue(bindingContext) as IPrPayslipEmploymentEntity;
    return payslipEmployment?.BalancingPeriods?.length > 0 && hasMonthlySalaryComponent(payslipEmployment);
};

export function hasMonthlySalaryComponent(payslipEmployment: IPrPayslipEmploymentEntity): boolean {
    return !!payslipEmployment?.SalaryComponents?.some(component => {
        const typeCode = component.TypeCode ?? component.Type?.Code;
        const regularSalaryTypeCode = component.RegularSalaryTypeCode ?? component.RegularSalaryType?.Code;
        return typeCode === PrSalaryComponentTypeCode.Regular && regularSalaryTypeCode === PrRegularSalaryTypeCode.Monthly;
    });
}

const formatSignedMinutesToHoursString = (minutes: number): string => {
    const formatted = formatMinutesToHoursString(Math.abs(minutes));
    if (minutes > 0) {
        return `+${formatted}`;
    }
    if (minutes < 0) {
        return `-${formatted}`;
    }
    return formatted;
};

function getBalancingPeriodTitle(period: IPrPayslipBalancingPeriodEntity): string {
    const weeks = getPluralizationLabel("Common:Time.Week", period.PeriodLengthInWeeks);
    return `${DateType.format(period.DateStart)} ${DASH_CHARACTER} ${DateType.format(period.DateEnd)} (${period.PeriodLengthInWeeks} ${weeks})`;
}

function getWorkingPatternForDate(payslipEmployment: IPrPayslipEmploymentEntity, date: Date): IPrWorkingPatternEntity {
    return payslipEmployment?.Employment?.TemporalPropertyBag
        ?.find(bag => getUtcDayjs(date).isBetween(getUtcDayjs(bag.DateValidFrom), getUtcDayjs(bag.DateValidTo), "day", "[]"))
        ?.WorkingPattern;
}

export function getBalancingPeriodFundSections(storage: FormStorage<IPrPayslipEntity>, bc: BindingContext): IFormTableListSection[] {
    const payslipEmployment = storage.getValue(bc) as IPrPayslipEmploymentEntity;

    return (payslipEmployment?.BalancingPeriods ?? []).map((period): IFormTableListSection => {
        const workingPattern = getWorkingPatternForDate(payslipEmployment, period.DateStart);

        return {
            rows: [
                { title: getBalancingPeriodTitle(period), value: null, isHeader: true, isFullWidth: true },
                {
                    title: i18next.t("Payslip:Form.BalancingPeriodWeeklyFund"),
                    value: workingPattern?.WorkingMinutesWeekly,
                    formatter: formatMinutesToHoursString
                },
                {
                    title: i18next.t("Payslip:Form.BalancingPeriodTotalFund"),
                    value: period.TotalFundInMinutes,
                    formatter: formatMinutesToHoursString
                },
                {
                    title: i18next.t("Payslip:Form.BalancingPeriodRemaining"),
                    value: period.RemainingInMinutes,
                    formatter: formatMinutesToHoursString
                }
            ]
        };
    });
}

export function getBalancingPeriodBilanceSections(storage: FormStorage<IPrPayslipEntity>, bc: BindingContext): IFormTableListSection[] {
    const payslipEmployment = storage.getValue(bc) as IPrPayslipEmploymentEntity;

    return (payslipEmployment?.BalancingPeriods ?? []).map((period): IFormTableListSection => ({
        rows: [
            {
                title: i18next.t("Payslip:Form.BalancingPeriodBalance"),
                value: i18next.t("Payslip:Form.BalancingPeriodSaldo")?.toString(),
                isHeader: true,
                additionalColumns: [
                    { id: "worked", value: i18next.t("Payslip:Form.BalancingPeriodWorked")?.toString(), align: TextAlign.Right },
                    { id: "planned", value: i18next.t("Payslip:Form.BalancingPeriodFund")?.toString(), align: TextAlign.Right }
                ]
            },
            {
                title: i18next.t("Payslip:Form.BalancingPeriodThisMonth"),
                value: period.ThisMonthBalanceInMinutes,
                formatter: formatSignedMinutesToHoursString,
                additionalColumns: [
                    {
                        id: "worked",
                        value: period.ThisMonthWorkedInMinutes,
                        formatter: formatMinutesToHoursString,
                        align: TextAlign.Right
                    },
                    {
                        id: "planned",
                        value: period.ThisMonthPlannedInMinutes,
                        formatter: formatMinutesToHoursString,
                        align: TextAlign.Right
                    }
                ]
            },
            {
                title: i18next.t("Payslip:Form.BalancingPeriodCumulative"),
                value: period.WholePeriodBalanceInMinutes,
                formatter: formatSignedMinutesToHoursString,
                additionalColumns: [
                    {
                        id: "worked",
                        value: period.WholePeriodWorkedInMinutes,
                        formatter: formatMinutesToHoursString,
                        align: TextAlign.Right
                    },
                    {
                        id: "planned",
                        value: period.WholePeriodPlannedInMinutes,
                        formatter: formatMinutesToHoursString,
                        align: TextAlign.Right
                    }
                ]
            }
        ]
    }));
}

export function getPayslipCurrencyPath(): string {
    return getEmployeeSalaryCurrencyPath(PrPayslipEntity.Employee);
}

export function isCustomPayslipDeduction(item: IPrPayslipDeductionEntity): boolean {
    return item.Deduction.TypeCode === PrDeductionTypeCode.Custom;
}

export const payslipPairDocumentAmountFormatter: TFormatterFn = (_value, args) => {
    return editableAmountLinkFormatter(args);
};

export const getEditableAmountDialogData = (storage: TSmartODataTableStorage<ISelectionWithEditableAmountCustomTableData>, item: IDocumentEntity, rowId: string): IEditableAmountDialogData => {
    const { pairedItems } = storage.getCustomData();
    const pairedItem = pairedItems[rowId];
    return {
        Amount: (pairedItem?.Amount ?? 0) * item.ExchangeRatePerUnit,
        Currency: item.Currency,
        ExchangeRatePerUnit: item.ExchangeRatePerUnit,
        TotalAmount: item.TransactionAmount,
        TransactionAmount: pairedItem?.TransactionAmount,
        TransactionCurrency: item.TransactionCurrency,
        NumberOurs: item.NumberOurs,
    };
};

export const resetPayslipsWorker = async (ids: number[]): Promise<void> => {
    await Promise.all(ids.map(resetPayslipToOriginal));
};

export async function resetPayslipToOriginal(id: number): Promise<boolean> {
    try {
        const path = `${RESET_PAYSLIP_API_URL}/${id}`;
        const response = await customFetch(path, getDefaultPostParams());
        if (response.ok) {
            return true;
        }
    } catch (e) {
        logger.error("Payslip reset failed", e);
    }
    return false;
}

export async function getPayslipsWithMasterDataChanges(storage: ITableViewBaseProps<IPrPayslipTableStorageCustomData>["storage"], period: IPrPayrollPeriodEntity): Promise<number[]> {
    const result = await storage.oData.getEntitySetWrapper(EntitySetName.PrPayslips).query()
        .select(PrPayslipEntity.Id)
        .filter(`(${getPayslipPeriodFilter(period)}) and ${PrPayslipEntity.MasterDataChanges}/any()`)
        .fetchData<IPrPayslipEntity[]>();

    return result.value?.map(ps => ps.Id) ?? [];
}

export async function hasMasterDataChanges(storage: ITableViewBaseProps<IPrPayslipTableStorageCustomData>["storage"], period: IPrPayrollPeriodEntity): Promise<boolean> {
    const result = await storage.oData.getEntitySetWrapper(EntitySetName.PrPayslips).query()
        .filter(`(${getPayslipPeriodFilter(period)}) and ${PrPayslipEntity.MasterDataChanges}/any()`)
        .top(1)
        .fetchData<IPrPayslipEntity[]>();

    return result.value?.length > 0;
}

export function renderDetectedChangeInfo(change: IPrPayslipMasterDataChangeEntity, storage: FormStorage): ReactElement {
    const route = getRouteByEntityType(change.EntityTypeCode as EntityTypeName);
    let extraInfo = "";
    if (change.CollectionName) {
        const entitySet = getEntitySetFromEntityType(storage.oData.metadata, change.EntityTypeCode as EntityTypeName);
        const bc = createBindingContext(`${entitySet}(${change.EntityId})`, storage.oData.metadata);
        const collectionTranslation = getPropertyTranslationSync(bc.navigate(change.CollectionName));
        extraInfo += ` - ${collectionTranslation}${change.CollectionItemDisplayName ? `: ${change.CollectionItemDisplayName}` : ""}`;
    }

    if (change.CollectionItemChangeTypeCode) {
        extraInfo += ` (${storage.t(`Payslip:CollectionItemChangeTypeCode.${change.CollectionItemChangeTypeCode}`)})`;
    }

    let name: string | ReactElement = `${change.EntityType?.Name}${change.EntityDisplayName ? `: ${change.EntityDisplayName}` : ""}`;
    if (isDefined(route)) {
        name = getDrillDownLink(name, {
            route: `${route}${change.EntityId ? `/${change.EntityId}` : ""}`,
            context: storage.context
        });
    }

    return <DetectedChangeLine data-testid={TestIds.DetectedChangeLine} key={change.Id}>- {name}{extraInfo}</DetectedChangeLine>;
}

interface IPrPayslipEmailError {
    ErrorCode: string;
    ErrorMessage?: string;
    EmployeeId?: number;
    FirstName?: string;
    LastName?: string;
}

interface ISendResponse {
    ProcessedCount: number;
    Errors?: IPrPayslipEmailError[];
}

interface IPayslipError {
    Message: string;
    Employees: string[];
}

interface ISendPayslipsRequestBody {
    PayslipIds: number[];
    Email?: string;
}

export async function sendPayslipsByEmail(ids: number[], email?: string): Promise<IAlertProps> {
    let processed: number;
    let hasErrors: boolean;
    const messages: string[] = [];

    try {
        const body: ISendPayslipsRequestBody = {
            PayslipIds: ids
        };
        if (email) {
            body.Email = email;
        }
        const response = await customFetch(`${SEND_PAYSLIP_EMAIL_URL}/SendPayslipsEmail`, {
            ...getDefaultPostParams(),
            body: JSON.stringify(body)
        });
        if (!response.ok) {
            const error = await parseResponse(response) as ODataError;
            return getAlertFromError(error);
        } else {
            const result = await response.json() as ISendResponse;
            processed = result.ProcessedCount;
            const errors = new Map<string, IPayslipError>();

            hasErrors = result.Errors?.length > 0 || processed !== ids.length;
            result.Errors?.forEach((err) => {
                let e = errors.get(err.ErrorCode);
                if (!e) {
                    const Message = i18next.exists(`Error:${err.ErrorCode}`) ? i18next.t(`Error:${err.ErrorCode}`)
                        : err.ErrorMessage ?? i18next.t("Common:Errors.UnknownError");
                    e = { Message, Employees: [] };
                }
                errors.set(err.ErrorCode, { ...e, Employees: [...e.Employees, universalPersonNameFormatter(err)] });
            });

            const shouldAppendName = ids?.length > 1;
            errors.entries().forEach(([_, err]) => {
                messages.push(err.Message + (shouldAppendName ? ` (${err.Employees.join(", ")})` : ""));
            });
        }
    } catch (e) {
        hasErrors = true;
    }

    if (hasErrors && !messages.length) {
        messages.push(i18next.t("Common:Errors.UnknownError"));
    }

    const hasSuccess = processed > 0;
    if (hasSuccess) {
        messages.unshift(i18next.t("Payslip:SendPayslipDialog.XSuccess", { count: processed }));
    }

    // show success message and refresh the table
    const status = hasSuccess && hasErrors ? Status.Warning : hasSuccess ? Status.Success : Status.Error;
    return getTableViewMessage(status, messages);
}

export const validityRangeFormatter = (validFrom: Date, validTo: Date): string => {
    const isValidFromStartOfMonth = validFrom ? isStartOfEndOfMonth(validFrom, "ValidFrom") : true;
    const isValidToEndOfMonth = validTo ? isStartOfEndOfMonth(validTo, "ValidTo") : true;
    if (isValidToEndOfMonth && isValidFromStartOfMonth) {
        return DASH_CHARACTER;
    } else if (!isValidToEndOfMonth && !isValidFromStartOfMonth) {
        return `${DateType.format(validFrom, DateFormat.dayAndMonth)} - ${DateType.format(validTo, DateFormat.dayAndMonth)}`;
    } else if (!isValidFromStartOfMonth) {
        return `${i18next.t("Common:Time.From")} ${DateType.format(validFrom, DateFormat.dayAndMonth)}`;
    }
    return `${i18next.t("Common:Time.To")} ${DateType.format(validTo, DateFormat.dayAndMonth)}`;
};